import { useCoreApi } from '@/composables/useApi'
import { GetMappedRemediesResp } from '@lifk/types'
import { Socket } from 'socket.io-client'
import useSWRV from 'swrv'
import { inject, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

interface GetMappedRemediesSWRVKey {
  name: string
  workshopUuid?: string
}

export const useGetMappedRemediesCurrentRoute = () => {
  const route = useRoute()
  const api = useCoreApi()
  const socket = inject<Socket>('socket')

  const currentRouteSWRVKey = ref({
    name: 'mapped-remedies',
    workshopUuid: route?.params?.uuid
  } as GetMappedRemediesSWRVKey)

  async function fetchMappedRemedies(workshopId: string) {
    const response = await api.get(`workshops/${workshopId}/remedies`)
    if (response.data.error) {
      return null
    }
    return response.data as GetMappedRemediesResp
  }

  const result = useSWRV(
    () =>
      `/${currentRouteSWRVKey.value.name}/${currentRouteSWRVKey.value.workshopUuid}`,
    async () => {
      if (currentRouteSWRVKey.value.workshopUuid) {
        return await fetchMappedRemedies(currentRouteSWRVKey.value.workshopUuid)
      } else {
        return null
      }
    },
    {
      dedupingInterval: 2000, // longer to accommodate a lot of frontend calculations called on initial load
      shouldRetryOnError: false
    }
  )

  watch(
    () => route?.params?.uuid,
    () => {
      currentRouteSWRVKey.value.workshopUuid = route.params?.uuid
        ? route.params.uuid.toString()
        : undefined
    }
  )

  const onMappedRemedyUpdated = (workshopUuid: string) => {
    if (currentRouteSWRVKey.value.workshopUuid === workshopUuid) {
      result.mutate()
    }
  }
  onMounted(() => {
    socket?.on('mapped-remedy-updated', onMappedRemedyUpdated)
  })
  onUnmounted(() => {
    socket?.off('mapped-remedy-updated', onMappedRemedyUpdated)
  })

  return result
}
